import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { SharedHeader } from "@/components/SharedHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { Phone, Mail, Building, ChevronDown, ChevronUp, DollarSign } from "lucide-react";

const ContactPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact/submit`, formData);
      toast.success("Message sent successfully! We'll be in touch soon.");
      setFormData({ name: "", email: "", subject: "", message: "" });
      setShowForm(false);
    } catch (err) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="contact-page">
      <SharedHeader 
        activeSection={null}
        setActiveSection={() => {}}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      <main className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="font-bebas text-4xl md:text-5xl tracking-wider text-white mb-12 text-center">
            CONTACT US
          </h1>
          
          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
            {/* Left Column - Get In Touch */}
            <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-8">
              <button
                onClick={() => setShowForm(!showForm)}
                className="flex items-center gap-2 w-full text-left mb-6 group"
                data-testid="toggle-form-btn"
              >
                <h2 className="font-bebas text-3xl tracking-wider text-[#7a6200] group-hover:text-[#C6A300] transition-colors">
                  GET IN TOUCH
                </h2>
                {showForm ? (
                  <ChevronUp className="w-6 h-6 text-[#7a6200]" />
                ) : (
                  <ChevronDown className="w-6 h-6 text-[#7a6200]" />
                )}
              </button>
              
              <p className="text-white font-ubuntu text-lg mb-8">
                Transform this vision into reality.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#7a6200]/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#7a6200]" />
                  </div>
                  <a href="tel:7133289736" className="text-[#A1A1AA] font-ubuntu hover:text-[#7a6200] transition-colors">
                    713.328.9736
                  </a>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#7a6200]/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#7a6200]" />
                  </div>
                  <a href="mailto:713Consulting@gmail.com" className="text-[#A1A1AA] font-ubuntu hover:text-[#7a6200] transition-colors">
                    713Consulting@gmail.com
                  </a>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#7a6200]/20 flex items-center justify-center flex-shrink-0">
                    <Building className="w-5 h-5 text-[#7a6200]" />
                  </div>
                  <div className="text-[#A1A1AA] font-ubuntu text-left">
                    <p>713 Consulting and Development</p>
                    <p>1942 W. Gray St., Box #1259</p>
                    <p>Houston, TX 77019</p>
                    <p>United States</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Support Our Mission */}
            <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-8">
              <h2 className="font-bebas text-3xl tracking-wider text-[#7a6200] mb-6">
                SUPPORT OUR MISSION
              </h2>
              
              <p className="text-white font-ubuntu text-lg mb-8">
                Help bring this innovative concept to life through donations, investments, or partnerships.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-center gap-4 p-4 bg-black/30 rounded border border-white/5">
                  <div className="w-12 h-12 rounded-full bg-[#7a6200]/20 flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-5 h-5 text-[#7a6200]" />
                  </div>
                  <div>
                    <p className="text-white font-ubuntu font-medium">Zelle</p>
                    <p className="text-[#A1A1AA] font-ubuntu">713Consulting@gmail.com</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 bg-black/30 rounded border border-white/5">
                  <div className="w-12 h-12 rounded-full bg-[#7a6200]/20 flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-5 h-5 text-[#7a6200]" />
                  </div>
                  <div>
                    <p className="text-white font-ubuntu font-medium">CashApp</p>
                    <p className="text-[#A1A1AA] font-ubuntu">$OriginalPower</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 bg-black/30 rounded border border-white/5">
                  <div className="w-12 h-12 rounded-full bg-[#7a6200]/20 flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-5 h-5 text-[#7a6200]" />
                  </div>
                  <div>
                    <p className="text-white font-ubuntu font-medium">Venmo</p>
                    <p className="text-[#A1A1AA] font-ubuntu">@KeithBolden</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form (Toggle) */}
          {showForm && (
            <div className="w-full lg:w-1/2" data-testid="contact-form-container">
              <div className="bg-[#0A0A0A] border border-[#7a6200]/30 rounded-lg p-8">
                <h3 className="font-bebas text-2xl tracking-wider text-white mb-6">
                  SEND US A MESSAGE
                </h3>
                
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="text-left">
                    <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">Name *</label>
                    <Input
                      type="text"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="input-dark h-12"
                      required
                      data-testid="contact-name"
                    />
                  </div>
                  
                  <div className="text-left">
                    <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">Email *</label>
                    <Input
                      type="email"
                      placeholder="Your email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="input-dark h-12"
                      required
                      data-testid="contact-email"
                    />
                  </div>
                  
                  <div className="text-left">
                    <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">Subject *</label>
                    <Input
                      type="text"
                      placeholder="Subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      className="input-dark h-12"
                      required
                      data-testid="contact-subject"
                    />
                  </div>
                  
                  <div className="text-left">
                    <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">Message *</label>
                    <Textarea
                      placeholder="Your message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="input-dark min-h-[120px]"
                      required
                      data-testid="contact-message"
                    />
                  </div>
                  
                  <div className="text-left">
                    <Button
                      type="submit"
                      className="btn-gold w-[40%]"
                      disabled={loading}
                      data-testid="contact-submit"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          SENDING...
                        </span>
                      ) : (
                        "SEND MESSAGE"
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="border-t border-white/10 bg-[#0A0A0A] py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-[#52525B] font-ubuntu text-sm">
            © 2026 All Rights Reserved | Board2Boston™ | 713 Consulting and Development
          </p>
          <p className="text-[#52525B] font-ubuntu text-xs mt-2">
            1942 W. Gray St., Box #1259 | Houston, TX 77019 | United States
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
